import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useRecurringRequestPreviewRefresh } from "@/features/RecurringRequests/RecurringRequestPreviewProvider";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  PatronRequestRecurrenceInput,
  useUpsertPatronRequestSeriesMutation,
} from "./__generated__/useUpsertPatronRequestSeriesMutation.graphql";

type UpsertOptions = {
  suppressSuccessToast?: boolean;
};

export const useUpsertPatronRequestSeries = () => {
  const { refresh: refreshPreview } = useRecurringRequestPreviewRefresh();
  const { showToast } = useToast();
  const [commit, isSaving] =
    useMutationWithError<useUpsertPatronRequestSeriesMutation>(
      UpsertPatronRequestSeriesMutationString
    );

  const upsertPatronRequestSeries = (
    sourceBookingId: string,
    recurrence: PatronRequestRecurrenceInput,
    onCompleted: (isStandaloneRequest: boolean) => void,
    previewConfirmation: {
      reviewedPlanToken: string;
    },
    options?: UpsertOptions
  ) => {
    commit({
      variables: {
        input: {
          sourceBookingId,
          recurrence,
          ...previewConfirmation,
        },
      },
      updater: (store) => {
        const sourceBooking = store.get(sourceBookingId);
        const bookingSeries = store
          .getRootField("upsertPatronRequestSeries")
          ?.getLinkedRecord("bookingSeries");

        if (sourceBooking && bookingSeries) {
          sourceBooking.setLinkedRecord(bookingSeries, "bookingSeries");
        }
      },
      onCompleted: (response) => {
        const payload = response.responsePayload;
        if (!payload?.success) {
          return;
        }

        if (!options?.suppressSuccessToast) {
          showToast({
            title: t`Recurrence saved`,
            semanticStyle: "success",
          });
        }
        onCompleted(payload.bookingSeries == null);
      },
      onCompletedErrorCustom: (errors, response) => {
        // A follow-up failure still returns the committed series; finish the save
        // and surface that failure separately.
        if (
          errors.some(
            (error) =>
              error.code === "recurring_request_series_post_commit_incomplete"
          )
        ) {
          showToast({
            description: t`Recurrence saved, but a follow-up notification or sync couldn't be completed automatically.`,
            semanticStyle: "warning",
            toastId: "upsert-patron-request-series-post-commit-warning",
          });
          onCompleted(response.responsePayload?.bookingSeries == null);
          return [];
        }

        refreshPreview();
        return errors;
      },
    });
  };

  return [upsertPatronRequestSeries, isSaving] as const;
};

const UpsertPatronRequestSeriesMutationString = graphql`
  mutation useUpsertPatronRequestSeriesMutation(
    $input: UpsertPatronRequestSeriesInput!
  ) {
    responsePayload: upsertPatronRequestSeries(input: $input) {
      success
      errors {
        field
        code
        message
      }
      createdOccurrenceCount
      updatedOccurrenceCount
      removedOccurrenceCount
      excluded {
        date
        reason
      }
      bookingSeries {
        id
        recurrenceType
        repeatInterval
        endCondition
        effectiveEndDate
        requestedOccurrenceCount
        bookings(first: 366, orderBy: [{ field: DATE_TIME, direction: DESC }]) {
          edges {
            node {
              id
              startSlot {
                agenda {
                  date
                }
              }
            }
          }
        }
      }
    }
  }
`;
